import {Capacitor} from '@capacitor/core';

type GoogleTagManagerWindow = Window & {
  dataLayer?: Array<Record<string, unknown>>;
};

export function injectGoogleTagManager(containerId?: string): void {
  if (!containerId) {
    return;
  }

  // TEMPORARY: GTM (and the OneTrust banner it loads) is not yet configured
  // to work correctly inside the Capacitor WebView (cookie domain mismatch -
  // see previous investigation). The same cookie-domain mismatch happens on
  // localhost (the banner reappears after accept/reject in a loop), so skip
  // loading it on native mobile builds and on localhost too, so the
  // broken/looping cookie banner doesn't block local testing. Remove this
  // guard once OneTrust confirms a working configuration for both.
  const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  if (Capacitor.isNativePlatform() || isLocalhost) {
    return;
  }

  const win = window as GoogleTagManagerWindow;
  win.dataLayer = win.dataLayer || [];

  // Expose the runtime platform to GTM *before* gtm.js loads, so container
  // tags/triggers (e.g. which OneTrust script to fire) can branch on it.
  // Coordinate the exact variable name/values below with whoever manages
  // the GTM container / OneTrust configuration.
  win.dataLayer.push({
    event: 'app_context',
    appPlatform: Capacitor.getPlatform(), // 'ios' | 'android' | 'web'
    isNativeApp: Capacitor.isNativePlatform(),
  });

  win.dataLayer.push({
    'gtm.start': Date.now(),
    event: 'gtm.js'
  });

  if (!document.querySelector(`script[data-gtm-container="${containerId}"]`)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
    script.setAttribute('data-gtm-container', containerId);

    const head = document.head;
    if (head.firstChild) {
      head.insertBefore(script, head.firstChild);
    } else {
      head.appendChild(script);
    }
  }

  if (!document.body || document.querySelector(`noscript[data-gtm-container="${containerId}"]`)) {
    return;
  }

  const noscript = document.createElement('noscript');
  noscript.setAttribute('data-gtm-container', containerId);

  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';

  noscript.appendChild(iframe);

  if (document.body.firstChild) {
    document.body.insertBefore(noscript, document.body.firstChild);
  } else {
    document.body.appendChild(noscript);
  }
}
